import React, {useEffect, useState} from 'react';
import {useLocation, useParams} from "wouter";
import {getHttpClient, setAccessToken, swrFetcher, UploadedAsset} from "../../utils/api.ts";
import useSWR, {useSWRConfig} from "swr";
import {FiPlus, FiTrash} from "react-icons/fi";
import Input from "../../components/form/Input.tsx";
import Select from "../../components/form/Select.tsx";
import Button from "../../components/form/Button.tsx";
import {useInvalidateAllQueries} from "../../components/hooks/useInvalidateAllQueries.ts";
import BackButton from "../../components/BackButton.tsx";
import Modal from '@/components/Modal.tsx';
import {closeModal, openModal} from "@/components/dispatch_functions.ts";
import AvatarWithUploader from "@/components/AvatarWithUploader.tsx";

const rolesAndDescription = {
    "owner": "Can manage workspace, users, accounts, posts and assets",
    "admin": "Can manage users, accounts, posts and assets",
    "manager": "Can manage posts and assets",
    // "publisher": "Can manage posts and assets",
    "creator": "Can manage post and assets, but cannot publish them to social handles",
}


function RenderRole({role}){
    switch (role) {
        case "owner":
            return <span className={"badge bg-secondary"}>Owner</span>
        case "admin":
            return <span className={"badge bg-primary"}>Admin</span>
        case "manager":
            return <span className={"badge bg-warning"}>Manager</span>
        // case "publisher":
        //     return <span cla<ssName={"badge bg-primary"}>Publisher</span>
        case "creator":
            return <span className={"badge bg-success"}>Creator</span>
    }
}

function WorkspaceEditor() {
    const id = useParams<{id: number}>().id;
    const {mutate: globalMutate} = useSWRConfig()
    const {data, mutate} = useSWR(id ? `/me/workspaces/${id}` : null, swrFetcher)
    const workspace = data?.workspace;
    const userRole = workspace?.settings?.role;

    const isOwnerOrEditor = userRole === 'owner' || userRole === 'manager';
    const [picture, setPicture] = useState<UploadedAsset>(workspace?.data.picture)
    const [name, setName] = useState('')
    const [saving, setSaving] = useState(false)
    const onSubmit = (e)=>{
        if(e){
            e.preventDefault()
            e.stopPropagation();
        }
        if(name && picture) {
            setSaving(true)
            getHttpClient().patch(`/me/workspaces/${id}`, {
                name,
                picture,
            }).then(() => {
                setSaving(false)
                closeModal('edit-workspace')
                mutate()
                globalMutate(`/me/workspaces/`)
            })
        }
    }
    useEffect(() => {
        setName(workspace?.data.name || 'No Name')
        setPicture(workspace?.data.picture)
    }, [workspace?.data.name, workspace?.data.picture]);
    return (
        <div>
            <BackButton to={"/settings"} />
            <Modal  id={"edit-workspace"}
                    disabled={!name}
                    confirmLoading={saving}
                    title={"Edit workspace"} onSubmit={onSubmit}>
                    <div className="mb-4 items-center flex justify-center">
                        <AvatarWithUploader
                            onUpload={(picture)=>{
                                setPicture(picture)
                            }}
                            size={200}
                            alt={name || ' '}
                            src={picture?.url} />
                    </div>
                    <Input type={"text"} label={"Workspace Name"} value={name} onChange={setName}/>
            </Modal>
            <div className="row">
                <div className="col-md-6">
                    <div style={{height:48}}>
                        <h2
                            className={"text-2xl"}
                            style={
                            isOwnerOrEditor ?
                            {textDecoration:'underline', textDecorationStyle:"dotted"} : null
                        }
                            onClick={isOwnerOrEditor ? ()=>openModal('edit-workspace') : undefined}>{name || workspace?.data.name}</h2>
                    </div>
                </div>
                {/*<div className="col-md-6">*/}
                {/*    /!*{*!/*/}
                {/*    /!*    isOwnerOrEditor && <div>*!/*/}
                {/*    /!*      <button className="btn btn-primary">Edit</button>*!/*/}
                {/*    /!*    </div>*!/*/}
                {/*    /!*}*!/*/}
                {/*</div>*/}
            </div>
            {
                isOwnerOrEditor ?
                <div>
                    <div className="mb-2 space-between">
                        <span>Users</span>
                        <AddUserToWorkspace id={id} mutate={mutate} />
                    </div>
                    <ul className="divide divide-y divide-base-200 border border-base-200 rounded">
                        {
                            workspace.users.map((i)=> {
                                return  <RenderUser
                                    key={String(i.id)}
                                    id={id}
                                    user={i} mutate={mutate} />
                            })
                        }
                    </ul>
                </div> : null
            }
            <div className="mt-4 text-end">
                <DeleteWorkspaceButton id={id} role={userRole} />
            </div>
        </div>
    );
}

function RenderUser({id, user,mutate}:{
    user: {
        email: string,
        profile?:{
            name: string,
        }
        settings: {
            role: string
        }
        id: number,
    },
    id: number
    mutate: ()=>void
}) {
    const [removing, setRemoving] = useState(false)
    const remove = ()=>{
        setRemoving(true)
        getHttpClient().delete(`/me/workspaces/${id}/users/${user.id}`)
            .then(()=>{
                setRemoving(false)
                closeModal("remove-user")
                mutate()
            })
    }
    return <>
    <Modal title={"Are you sure ?"}
           okText={"Remove"}
           id={"remove-user"}
           submitBtnClass={"btn btn-error"}
           onSubmit={remove} confirmLoading={removing}>
        <p>Are you sure you want to remove this user ?</p>
    </Modal>
    <li className="p-3 space-between">
        <span className={"gap-4 align-items-center"}>
            <div className={"flex gap-2"}>
                <span>{user?.profile?.name}</span>
                <div>{user?.profile?.name ? <small className={"text-muted"}>{user.email}</small> : <span>{user.email}</span>}</div>
            </div>
            <RenderRole role={user.settings.role}/>
        </span>
        {
            user.settings.role !== "owner" ?
            <Button className={"btn btn-link text-error"} onClick={()=>{
                openModal("remove-user")
            }}><FiTrash/></Button> : null
        }
    </li>
    </>
}

function AddUserToWorkspace({id, mutate}) {
    const [email, setEmail] = React.useState('')
    const [role, setRole] = React.useState('creator')
    const [adding, setAdding] = React.useState(false)
    const [name,setName] = useState('')

    const onAddUser = ()=>{
        setAdding(true)
        getHttpClient().post(`/me/workspaces/${id}/users`,{
            email,
            role,
            name,
        }).then(()=>{
            setAdding(false)
            mutate()
            closeModal('add-user')
        })
    }
    return <>
        <span><button onClick={()=>openModal('add-user')} className={"btn btn-outline-primary btn-sm"}><FiPlus/> Add</button></span>
        <Modal
               confirmLoading={adding}
               okText={"Submit"}
               onSubmit={onAddUser}
               id={"add-user"}
               title={"Add new user"}>
            <p className={"mb-3"}>Add a new user to this workspace</p>
            <div className="mb-3">
                <Input type={"name"} label={"Name"} placeholder={"eg. John"} value={name} onChange={setName}/>
            </div>
            <div className="mb-3">
                <Input type={"email"} label={"Email Address"} placeholder={"eg. john@example.com"} value={email} onChange={setEmail}/>
            </div>
            <div className={"space-between"}>
                <label className={"label"}>Role</label>
                <div>
                    <Select options={[
                        {label: 'Creator', value: 'creator'},
                        // {label: 'Publisher', value: 'publisher'},
                        {label: 'Manager', value: 'manager'},
                        {label: 'Admin', value: 'admin'},
                    ]} value={role} onChange={setRole}>
                    </Select>

                </div>
            </div>
            <div className={"text-sm opacity-75 text-center mt-2"}>{rolesAndDescription[role]}</div>
        </Modal>
    </>
}

function DeleteWorkspaceButton({id, role}: {
    id: number,
    role: string
}) {
    const [deleting, setDeleting] = useState(false);
    const invalidateAllQueries = useInvalidateAllQueries()
    const {data} = useSWR('/me', swrFetcher)
    const current_workspace_id = data?.user?.workspace?.id;
    const [, navigate] = useLocation()
    const del = ()=>{
        setDeleting(true)
        getHttpClient().delete(`/me/workspaces/${id}`)
            .then(({data})=>{
                setDeleting(false)
                closeModal('delete-workspace')
                if(current_workspace_id === id) {
                    setAccessToken(data.access_token);
                    invalidateAllQueries().then(() => {
                        navigate('/')
                    })
                }else{
                    history.back()
                }
            })
    }
    return   <>
        <Modal title={"Are you sure ?"}
               submitBtnClass={"btn btn-error"}
               okText={"Delete"}
               id={"delete-workspace"}
               onSubmit={del} confirmLoading={deleting}
        >
            <p>Are you sure you want to delete this workspace ?</p>
            {
                role === 'owner' ? <p>This will delete all the content (assets, posts, statistics) associated with this workspace</p> :
                    <p>Since you are not the owner, this will only remove your account from this workspace</p>
            }
        </Modal>
             <Button
                 onClick={()=>openModal('delete-workspace')}
                 className={"btn btn-link text-error d-inline-flex gap-2 align-items-center no-underline"}>
                <FiTrash /> Delete workspace
            </Button>
        </>
}

export default WorkspaceEditor;
